/**
 * XHR-based fetch implementation used as a bypass for environments
 * where window.fetch is wrapped by a proxy (e.g. the Lovable preview's
 * lovable.js fetch interceptor that breaks Supabase auth POSTs).
 *
 * Only the subset of fetch semantics Supabase relies on is implemented:
 * method, headers, body (string / Blob / ArrayBuffer / FormData),
 * response status/statusText/headers/body, AbortSignal.
 */
export const xhrFetch: typeof fetch = (input: RequestInfo | URL, init: RequestInit = {}) => {
  return new Promise<Response>((resolve, reject) => {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
        ? input.toString()
        : (input as Request).url;
    const method = (init.method ?? "GET").toUpperCase();

    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.responseType = "arraybuffer";

    // Headers
    const headers = new Headers(init.headers ?? {});
    headers.forEach((value, key) => {
      try {
        xhr.setRequestHeader(key, value);
      } catch {
        /* some headers (e.g. content-length) are forbidden */
      }
    });

    // Abort support
    if (init.signal) {
      if (init.signal.aborted) {
        xhr.abort();
        return reject(new DOMException("Aborted", "AbortError"));
      }
      init.signal.addEventListener("abort", () => xhr.abort());
    }

    xhr.onload = () => {
      const rawHeaders = xhr.getAllResponseHeaders();
      const responseHeaders = new Headers();
      rawHeaders
        .trim()
        .split(/\r?\n/)
        .forEach((line) => {
          const idx = line.indexOf(":");
          if (idx > -1) {
            responseHeaders.append(
              line.slice(0, idx).trim(),
              line.slice(idx + 1).trim()
            );
          }
        });

      const body =
        method === "HEAD" || xhr.status === 204 || xhr.status === 205
          ? null
          : (xhr.response as ArrayBuffer);

      resolve(
        new Response(body, {
          status: xhr.status,
          statusText: xhr.statusText,
          headers: responseHeaders,
        })
      );
    };

    xhr.onerror = () => reject(new TypeError("Network request failed"));
    xhr.ontimeout = () => reject(new TypeError("Network request timed out"));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));

    // Body
    const body = init.body as BodyInit | null | undefined;
    if (body == null) {
      xhr.send();
    } else if (
      typeof body === "string" ||
      body instanceof Blob ||
      body instanceof ArrayBuffer ||
      body instanceof FormData ||
      body instanceof URLSearchParams
    ) {
      xhr.send(body as any);
    } else if (ArrayBuffer.isView(body)) {
      xhr.send(body as any);
    } else {
      // Fallback: stringify
      xhr.send(String(body));
    }
  });
};
